/**
 * normaliseSalespeople — canonical normaliser for the salesperson field on
 * customer records. Handles all known key variants and value shapes.
 *
 * DEC-50 (alias pair #13): the DynamoDB customer record may store salesperson
 * data under any of these keys (legacy + current):
 *   - `salespeople`    — legacy (no 'r' in 'persons'); written by older code
 *   - `salespersons`   — current canonical key (AddCustomerDrawer, CustomerFormPanel)
 *   - `salesperson`    — singular legacy (one assigned salesperson)
 *   - `salespersonName`— singular camelCase (quotes API projection)
 *   - `salesperson_name`— singular snake_case variant
 *   - `salesperson_id` — oldest legacy (ID-only, no name)
 *
 * Value shapes per key:
 *   - string[]         — TypeaheadChips form state (names as strings)
 *   - {id, name}[]     — normalised object array
 *   - string           — singular legacy (one name)
 *   - {id, name}       — singular object
 *
 * Output: always `[{ id: string, name: string }]` (may be empty).
 *
 * Consumers (2, registered in COMPONENT_MAP.md):
 *   - GoldenCustomerCard.normaliseSalespeople (display)
 *   - quotePrefill.prefillFromCustomer (quote builder prefill)
 *
 * Writer sweep (2026-08-01): both current writers (AddCustomerDrawer:258,
 * CustomerFormPanel:182) write `salespersons` (with 'r'). No active writer
 * produces `salespeople` (without 'r') — that key is legacy data only.
 * Backend (`cmd/customers/main.go`) stores whatever the frontend sends as
 * `map[string]interface{}` — schema-less.
 *
 * @param {object} customer — raw customer record from API
 * @returns {{ id: string, name: string }[]}
 */
export function normaliseSalespeople(customer) {
  if (!customer) return [];

  // Priority order matches GoldenCustomerCard (which displays correctly).
  const raw = (Array.isArray(customer.salespeople) && customer.salespeople.length > 0)
    ? customer.salespeople
    : (Array.isArray(customer.salespersons) && customer.salespersons.length > 0)
    ? customer.salespersons
    : (Array.isArray(customer.sales_persons) && customer.sales_persons.length > 0)
    ? customer.sales_persons
    : (customer.salesperson || customer.salespersonName || (!customer.salesperson_id && customer.salesperson_name))
      ? [customer.salesperson || customer.salespersonName || customer.salesperson_name]
      : customer.salesperson_id
        ? [{ id: customer.salesperson_id, name: customer.salesperson_name || '' }]
        : [];

  if (!Array.isArray(raw)) return [];

  return raw
    .filter(sp => sp && (typeof sp === 'string' ? sp.trim() : true))
    .map((sp) => {
      if (typeof sp === 'string') return { id: sp, name: sp };
      return {
        id: sp.id || sp.user_id || sp.salesperson_id || sp.name || '',
        name: sp.name || sp.full_name || sp.salesperson_name || sp.email || sp.id || '',
      };
    })
    .filter(sp => sp.id || sp.name);
}

// IDI-164: a value that looks like an identifier rather than a person's name
// (UUID, bare numeric legacy User_ID, or a `USER#…` key). Never rendered.
const RAW_ID_RE = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+|USER#.*)$/i;
export function looksLikeRawId(value) {
  return typeof value === 'string' && RAW_ID_RE.test(value.trim());
}

/**
 * resolveQuoteSalespeople — the Quotes-list Salesperson reader (IDI-164).
 *
 * Precedence (Adrian 09-30 / 10-20, IDI-342):
 *   1. the named entries of the quote's salespersons[] (an entry carrying
 *      only an id is resolved against `users`; unresolvable entries drop);
 *   2. quote.salespersonName — resolved SERVER-SIDE from assignedTo
 *      (legacy Quote_AssignedTo), never from createdBy;
 *   3. [] — the caller renders "—".
 * A raw id (UUID / legacy numeric / USER#) is never returned as a name.
 *
 * @param {object} quote
 * @param {Array<{id:string,name?:string,email?:string}>} [users]
 * @returns {{ id: string, name: string }[]}
 */
export function resolveQuoteSalespeople(quote, users = []) {
  if (!quote) return [];
  const list = (Array.isArray(quote.salespersons) && quote.salespersons.length > 0)
    ? quote.salespersons
    : (Array.isArray(quote.salespeople) ? quote.salespeople : []);
  const userList = Array.isArray(users) ? users : [];
  const lookup = (id) => {
    if (!id) return '';
    const u = userList.find((x) => x && (x.id === id || x.userId === id || x.legacyId === id));
    if (!u) return '';
    const full = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '';
    return looksLikeRawId(full) ? '' : full;
  };

  const named = [];
  for (const sp of list) {
    if (!sp) continue;
    let id = '';
    let name = '';
    if (typeof sp === 'string') {
      id = sp.trim();
      name = looksLikeRawId(id) ? lookup(id) : id;
    } else {
      id = sp.id || sp.user_id || sp.salesperson_id || '';
      const own = sp.name || sp.full_name || sp.salesperson_name || '';
      name = own && !looksLikeRawId(own) ? own : lookup(id);
      if (!name && sp.email && !looksLikeRawId(sp.email)) name = sp.email;
    }
    name = (name || '').trim();
    if (name) named.push({ id: id || name, name });
  }
  if (named.length > 0) return named;

  const spName = typeof quote.salespersonName === 'string' ? quote.salespersonName.trim() : '';
  if (spName && !looksLikeRawId(spName)) return [{ id: spName, name: spName }];
  return [];
}

/**
 * quoteDateValue — the Quote Date column value (IDI-164, the PDF rule):
 * the quote's own `date`, else its createdAt.
 */
export function quoteDateValue(quote) {
  if (!quote) return undefined;
  return quote.date || quote.createdAt || quote.created_at;
}
